import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
	Form,
	Select,
	Row,
	Col,
	Space,
	Modal,
	Card,
	Divider,
	Typography,
	InputNumber,
	Switch,
	Tooltip,
	Input,
	TimePicker,
} from "antd";
import {
	InnerForm,
	ProductData,
	PublishSettings,
	Container,
	CardContainer,
	InnerBlocks,
} from "./styles";
import { CloseCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useAuth } from "../../../../../contexts/authContext";
import {
	openNotificationWithIcon,
	TYPE,
} from "../../../../../utils/notificationToast";
import Main from "../../../../layout/main/Main";
import { COLORS, Typo } from "../../../../layout/theme";
import SignInTemplate from "../../../../layout/sign-in-template/SignInTemplate";
import { get_clients_name_list_filtered_seller_IA } from "../../../../../api/endpoints/clients";
import { create_agent_config } from "../../../../../api/endpoints/agents";
import moment from "moment";
import { CONSTANTS } from "../../../../../utils/constants";
import AntdSelect from "../../../../common/antd-select/AntdSelect";

const { Option } = Select;
const GUTTER = 10;

const formatDeriveToSupportKeywords = (keywords) => {
	const formattedKeywords = (keywords || [])
		.map((keyword) => keyword?.trim())
		.filter(Boolean)
		.join(", ");

	return formattedKeywords || null;
};

export default function CreateAgentConfig({ clientId }) {
	const [form] = Form.useForm();
	const { userdata } = useAuth();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const uri = searchParams.get("uri");

	const [submitting, setSubmitting] = useState(false);
	const [clients, setClients] = useState(null);
	const [selectedClient, setSelectedClient] = useState(clientId);

	const typeOfCommunicationValue = Form.useWatch("typeOfCommunication", form);
	const sellerProfileValue = Form.useWatch("sellerProfile", form);
	const humanAutoReturnEnabledValue = Form.useWatch(
		"humanAutoReturnEnabled",
		form,
	);

	useEffect(() => {
		const fetch_market_clients = async () => {
			const res = await get_clients_name_list_filtered_seller_IA();
			setClients(
				res
					?.filter((c) => c.isApproved)
					?.map((r) => ({
						value: r.id,
						label: r.name,
					})),
			);
		};

		const fetch_defaul_values = () => {
			form.setFieldsValue({
				name: "Luis",
				language: "Español",
				typeOfCommunication: "amable",
				sellerProfile: "upselling_suave",
				activeOffers: true,
				activeSuggesteds: true,
				state: "ACTIVE",
				autoMessageEveryDays: 7,
				autoMessageHour: moment("09:00", "HH:mm"),
				supportEmail: "correo@soporte.com",
				deriveToSupportKeywords: [],
				humanAutoReturnEnabled: false,
				humanAutoReturnAfterMinutes: 30,
				humanTakeoverNoticeText:
					"Un humano está atendiendo esta conversación. Te responderemos en breve.",
				humanReleaseNoticeText:
					"El agente virtual vuelve a atender esta conversación.",
			});
		};

		fetch_market_clients();
		fetch_defaul_values();
	}, []);

	const onFinish = async (values) => {
		try {
			setSubmitting(true);

			const defaultDays = 7;
			const defaultHour = "09:00";
			const agentConfigPayload = {
				laboratoryId: selectedClient,
				name: values.name,
				language: values.language,
				clientName: values.clientName,
				typeOfCommunication: values.typeOfCommunication,
				sellerProfile: values.sellerProfile,
				state: values.state === "DELETED",
				activeOffers: !!values.activeOffers,
				activeSuggesteds: !!values.activeSuggesteds,
				activeFreeCommunication: true,
				autoMessageEveryDays: values.autoMessageEveryDays ?? defaultDays,
				autoMessageHour: values.autoMessageHour
					? values.autoMessageHour.format("HH:mm")
					: defaultHour,
				deriveToSupportOnOptOut: !!values.deriveToSupportOnOptOut,
				deriveToSupportKeywords: formatDeriveToSupportKeywords(
					values.deriveToSupportKeywords,
				),
				humanAutoReturnEnabled: !!values.humanAutoReturnEnabled,
				humanAutoReturnAfterMinutes: values.humanAutoReturnAfterMinutes ?? 30,
				humanTakeoverNoticeText: values.humanTakeoverNoticeText?.trim() || null,
				humanReleaseNoticeText: values.humanReleaseNoticeText?.trim() || null,
			};

			const result = await create_agent_config(agentConfigPayload);

			openNotificationWithIcon(
				TYPE.SUCCESS,
				"Configuracion creada con éxito",
				"La configuracion del agente se creo con éxito",
			);
			navigate(`/admin/laboratoryAgentConfigs/${result}?uri=${uri}`);
		} catch (error) {
			openNotificationWithIcon(
				TYPE.ERROR,
				"Error al crear los datos",
				error?.response?.data?.title ||
					"Un error ocurrio al intentar crear los datos, contacte con soporte a soporte@checkpos.com.",
			);
		} finally {
			setSubmitting(false);
		}
	};

	const handleBack = () => {
		Modal.confirm({
			title: "¿Seguro que quiere volver sin guardar?",
			icon: <CloseCircleOutlined style={{ color: "red" }} />,
			content:
				"Si regresa sin guardar se perderán los datos que hayan sido cargados",
			okText: "Sí",
			okType: "danger",
			cancelText: "No",
			onOk: () => {
				navigate("/admin/laboratoryAgentConfigs?uri=" + uri);
			},
		});
	};

	const handleClientSelect = (value, object) => {
		if (userdata.isOneMarket) setSelectedClient(object.value);
	};

	const getTypeOfCommunicationDescription = (value) => {
		switch (value) {
			case "amable":
				return "Tu estilo de comunicación es amable y cercano: saludas cordialmente, agradeces y mantienes un tono respetuoso y positivo.";
			case "conciso_directo":
				return "Tu estilo de comunicación es conciso y directo: vas al punto y respondes en pocas frases.";
			case "didactico":
				return "Tu estilo de comunicación es didáctico: explicas los pasos y recomendaciones de forma clara y ordenada.";
			case "formal":
				return "Tu estilo de comunicación es formal y corporativo: usas un tono respetuoso, profesional, sin modismos ni emojis.";
			case "informal_cercano":
				return "Tu estilo de comunicación es informal y cercano: puedes usar un tono de conversación cotidiano, algún emoji moderado cuando sea apropiado, pero sin perder profesionalismo ni claridad.";
			case "entusiasta":
				return "Tu estilo de comunicación es entusiasta: celebras los avances del pedido, reconoces cuando el cliente toma decisiones inteligentes para su negocio y mantienes un tono motivador sin exagerar.";
			case "analitico":
				return "Tu estilo de comunicación es analítico: cuando corresponde, destacas números clave (descuentos, cantidades sugeridas, comparaciones simples) de forma clara, resaltando el impacto económico para la farmacia.";
			default:
				return "Tu estilo de comunicación es amable y claro.";
		}
	};

	const getSellerProfileDescription = (value) => {
		switch (value) {
			case "consultivo":
				return "Tu perfil de vendedor es consultivo, priorizando las necesidades reales del cliente a partir del pedido sugerido.";
			case "upselling_suave":
				return "Tu perfil de vendedor es orientado a ventas suave: cuando detectas un interés de compra, propones formatos más grandes siempre como sugerencia opcional sin presionar.";
			case "cross_selling":
				return "Tu perfil de vendedor es orientado a ventas cruzadas: propones productos complementarios lógicos basándote en el nombre.";
			case "ofertas_volumen":
				return "Tu perfil de vendedor es orientado a ofertas y volumen: destacas siempre las ofertas vigentes, escalas por volumen y oportunidades proponiendo cantidades que aprovechen mejor el descuento.";
			default:
				return "Tu perfil de vendedor es consultivo, priorizando las necesidades reales del cliente a partir del pedido sugerido.";
		}
	};

	return (
		<Main>
			<Main.Header>
				<Main.Breadcrumb>
					<Main.BreadcrumbItem>
						{uri ? (
							<Link to={"/" + uri}>Configuración General</Link>
						) : (
							<Link to="/">Home</Link>
						)}
					</Main.BreadcrumbItem>
					<Main.BreadcrumbSeparator />
					<Main.BreadcrumbItem>
						<Link to={"/admin/laboratoryAgentConfigs?uri=" + uri}>
							Configuracion de agente
						</Link>
					</Main.BreadcrumbItem>
					<Main.BreadcrumbSeparator />
					<Main.ActiveBreadcrumbItem>
						Crear configuracion
					</Main.ActiveBreadcrumbItem>
				</Main.Breadcrumb>
			</Main.Header>

			<Main.Body>
				<Form
					form={form}
					name="lab-suggestions"
					onFinish={onFinish}
					autoComplete="off"
					requiredMark={false}
					layout="vertical"
				>
					<InnerBlocks>
						<InnerForm>
							<ProductData>
								<CardContainer>
									<Row gutter={GUTTER}>
										<Col span={24}>
											<Form.Item
												label={
													<label
														style={{ color: COLORS.Primary, fontWeight: "600" }}
													>
														Laboratorio
													</label>
												}
												name="laboratoryId"
												rules={[
													{
														required: true,
														message: "Este campo es obligatorio",
													},
												]}
											>
												<Select
													showSearch
													placeholder="Elegi un laboratorio"
													optionFilterProp="children"
													onChange={handleClientSelect}
													value={
														selectedClient !== "null" ? selectedClient : "..."
													}
													filterOption={(input, option) =>
														(option?.label ?? "")
															.toLowerCase()
															.includes(input.toLowerCase())
													}
													options={clients}
													disabled={!userdata.isOneMarket}
												/>
											</Form.Item>
										</Col>
									</Row>
								</CardContainer>
							</ProductData>

							<PublishSettings>
								<Container>
									<Space
										direction="vertical"
										size="middle"
										style={{ display: "flex" }}
									>
										<Form.Item>
											<SignInTemplate.AntdButton
												type="Primary"
												color="white"
												bg="Primary"
												htmlType="submit"
												loading={submitting}
												block
											>
												Guardar Cambios
											</SignInTemplate.AntdButton>
										</Form.Item>
										<SignInTemplate.AntdButton
											type="Primary"
											onClick={handleBack}
											loading={submitting}
											block
										>
											<Typo type="danger" level={6}>
												Volver sin guardar
											</Typo>
										</SignInTemplate.AntdButton>
									</Space>
								</Container>
							</PublishSettings>
						</InnerForm>
						<CardContainer>
							<Card
								title={<Typo level={5}>Configuración</Typo>}
								bodyStyle={{ paddingTop: 16 }}
							>
								<Row gutter={GUTTER}>
									{/* Columna 1: Datos */}
									<Col xs={24} md={12} lg={5}>
										<Card
											size="small"
											type="inner"
											title="Identidad"
											bordered={true}
										>
											<Form.Item
												label={
													<Typo level={16} style={{ whiteSpace: "nowrap" }}>
														Nombre
													</Typo>
												}
												name="name"
												rules={[{}]}
											>
												<Input style={{ width: "100%" }} />
											</Form.Item>
											<Form.Item
												label={
													<Typo level={16} style={{ whiteSpace: "nowrap" }}>
														Empresa
													</Typo>
												}
												name="clientName"
												rules={[{}]}
											>
												<Input style={{ width: "100%" }} />
											</Form.Item>
											<Form.Item
												label={
													<Space align="center">
														<Typo level={16} style={{ whiteSpace: "nowrap" }}>
															Tipo de comunicacion
														</Typo>
														<Tooltip
															title={getTypeOfCommunicationDescription(
																typeOfCommunicationValue,
															)}
															placement="top"
														>
															<InfoCircleOutlined
																style={{ color: COLORS.Primary }}
															/>
														</Tooltip>
													</Space>
												}
												name="typeOfCommunication"
												rules={[{}]}
											>
												<Select
													showSearch
													placeholder="Elegi un tipo"
													optionFilterProp="children"
													filterOption={(input, option) =>
														(option?.label ?? "")
															.toLowerCase()
															.includes(input.toLowerCase())
													}
													options={CONSTANTS.AGENT_SELLER_TYPE_OF_COMMUNICATION}
												/>
											</Form.Item>
											<Form.Item
												label={
													<Space align="center">
														<Typo level={16} style={{ whiteSpace: "nowrap" }}>
															Perfil de vendedor
														</Typo>
														<Tooltip
															title={getSellerProfileDescription(
																sellerProfileValue,
															)}
															placement="top"
														>
															<InfoCircleOutlined
																style={{ color: COLORS.Primary }}
															/>
														</Tooltip>
													</Space>
												}
												name="sellerProfile"
												rules={[{}]}
											>
												<Select
													showSearch
													placeholder="Elegi un tipo"
													optionFilterProp="children"
													filterOption={(input, option) =>
														(option?.label ?? "")
															.toLowerCase()
															.includes(input.toLowerCase())
													}
													options={CONSTANTS.AGENT_SELLER_PROFILES}
												/>
											</Form.Item>
										</Card>
									</Col>

									{/* Columna 2: Filtrado y opciones */}
									<Col xs={24} md={12} lg={5}>
										<Card
											size="small"
											type="inner"
											title="Opciones"
											bordered={true}
										>
											<Form.Item
												label="Ofrecer ofertas"
												name="activeOffers"
												valuePropName="checked"
											>
												<Switch />
											</Form.Item>
											<Form.Item
												label="Ofrecer sugeridos"
												name="activeSuggesteds"
												valuePropName="checked"
											>
												<Switch />
											</Form.Item>
											<Form.Item
												label={
													<Typo level={16} style={{ whiteSpace: "nowrap" }}>
														Idioma
													</Typo>
												}
												name="language"
												rules={[{}]}
											>
												<Input style={{ width: "100%" }} disabled />
											</Form.Item>
											<Form.Item
												label="Estado configuracion"
												name="state"
												rules={[
													{
														required: true,
														message: "Este campo es obligatorio",
													},
												]}
											>
												<Select allowClear placeholder="Estado">
													<Option value="ACTIVE">Activa</Option>
													<Option value="DELETED">Inactiva</Option>
												</Select>
											</Form.Item>
										</Card>
									</Col>

									{/* Columna 3: Mensajería automática */}
									<Col xs={24} md={12} lg={5}>
										<Card
											size="small"
											type="inner"
											title="Mensajería automática"
											bordered
										>
											<Form.Item
												label="Cada cuántos días"
												name="autoMessageEveryDays"
												tooltip="Intervalo entre mensajes automáticos"
												rules={[
													({ getFieldValue }) => ({
														validator(_, v) {
															if (v == null || v === "")
																return Promise.resolve();
															if (Number.isInteger(v) && v >= 1 && v <= 365)
																return Promise.resolve();
															return Promise.reject(
																"Ingrese un entero entre 1 y 365",
															);
														},
													}),
												]}
											>
												<InputNumber
													min={1}
													max={365}
													style={{ width: "100%" }}
													placeholder="p. ej. 7"
												/>
											</Form.Item>

											<Form.Item
												label="Hora de envío"
												name="autoMessageHour"
												tooltip="Hora local del cliente (HH:mm)"
											>
												<TimePicker format="HH:mm" style={{ width: "100%" }} />
											</Form.Item>

											<Form.Item
												label="Correo de Soporte"
												name="supportEmail"
												tooltip="Correo para derivacion a soporte"
											>
												<Input style={{ width: "100%" }} />
											</Form.Item>

											<Form.Item
												label="Aviso de bajas"
												name="deriveToSupportOnOptOut"
												valuePropName="checked"
											>
												<Switch />
											</Form.Item>

											<Form.Item
												label="Keywords para derivar a soporte"
												name="deriveToSupportKeywords"
												tooltip={CONSTANTS.DERIVE_TO_SUPPORT_KEYWORDS_HELP_TEXT}
											>
												<AntdSelect
													mode="tags"
													style={{ width: "100%" }}
													placeholder={
														CONSTANTS.DERIVE_TO_SUPPORT_KEYWORDS_PLACEHOLDER
													}
													tokenSeparators={[",", "\n"]}
													open={false}
												/>
											</Form.Item>
										</Card>
									</Col>

									{/* Columna 4: Control humano */}
									<Col xs={24} md={12} lg={5}>
										<Card
											size="small"
											type="inner"
											title="Control humano"
											bordered
										>
											<Form.Item
												label="Retorno automático"
												name="humanAutoReturnEnabled"
												valuePropName="checked"
												tooltip="Si está activo, el backend puede devolver la conversación al agente cuando llegue un nuevo mensaje después de la inactividad configurada."
											>
												<Switch />
											</Form.Item>

											<Form.Item
												label="Minutos de inactividad"
												name="humanAutoReturnAfterMinutes"
												dependencies={["humanAutoReturnEnabled"]}
												rules={[
													({ getFieldValue }) => ({
														validator(_, v) {
															if (!getFieldValue("humanAutoReturnEnabled"))
																return Promise.resolve();
															if (Number.isInteger(v) && v >= 1 && v <= 1440)
																return Promise.resolve();
															return Promise.reject(
																"Ingrese un entero entre 1 y 1440",
															);
														},
													}),
												]}
											>
												<InputNumber
													min={1}
													max={1440}
													style={{ width: "100%" }}
													placeholder="p. ej. 30"
													disabled={!humanAutoReturnEnabledValue}
												/>
											</Form.Item>

											<Form.Item
												label="Mensaje inicio chat con Humano"
												name="humanTakeoverNoticeText"
												tooltip="Mensaje enviado cuando un humano toma el control. Tambien se usa para enviar un recordatorio a los 30 minutos."
											>
												<Input.TextArea
													rows={3}
													maxLength={500}
													showCount
													placeholder="Un humano está atendiendo esta conversación. Te responderemos en breve."
												/>
											</Form.Item>

											<Form.Item
												label="Mensaje fin chat con Humano"
												name="humanReleaseNoticeText"
												tooltip="Mensaje enviado cuando un humano reactiva el agente para la conversacion."
											>
												<Input.TextArea
													rows={3}
													maxLength={500}
													showCount
													placeholder="El agente virtual vuelve a atender esta conversación."
												/>
											</Form.Item>
										</Card>
									</Col>
								</Row>
							</Card>
						</CardContainer>
					</InnerBlocks>
				</Form>
			</Main.Body>
		</Main>
	);
}
